<template>
    <KNavigation
        class="site-header"
        :items="navigation"
        :link-as="nuxtLink"
        sticky
    >
        <template #logo>
            <NuxtLink
                to="/"
                :aria-label="`${config.brand_name || 'WeAreTanzania'} — home`"
            >
                <img
                    :src="logoSrc"
                    :alt="`Logo of ${config.brand_name || 'WeAreTanzania'}`"
                    height="32"
                >
            </NuxtLink>
        </template>

        <template #search>
            <SiteSearch
                label="Search the site"
                placeholder="Search"
                @search="onSearch"
            />
        </template>
    </KNavigation>

    <main class="page">
        <slot />
    </main>

    <div
        v-if="config.ai_contact && !chatReady"
        class="chat-trigger"
    >
        <KButton
            :disabled="chatLoading"
            :aria-busy="chatLoading"
            :aria-label="chatLabel"
            :title="chatLabel"
            size="lg"
            @click="openChat"
        >
            <template #icon-start>
                <Icon
                    :name="chatIcon"
                    aria-hidden="true"
                />
            </template>
        </KButton>
    </div>

    <KFooter
        class="site-footer"
        :title="config.footer_text"
        :columns="footerColumns"
        :social-links="socialLinks"
        :copyright="`© ${config.brand_name}. All Rights Reserved.`"
        credits="Built and optimized by VE Media."
        :link-as="nuxtLink"
    >
        <template #logo>
            <NuxtLink
                to="/"
                :aria-label="`${config.brand_name || 'WeAreTanzania'} — home`"
            >
                <img
                    :src="logoSrc"
                    :alt="`Logo of ${config.brand_name || 'WeAreTanzania'}`"
                    height="32"
                    loading="lazy"
                >
            </NuxtLink>
        </template>

        <template #social-icon="{ platform }">
            <Icon
                :name="socialIcon(platform)"
                aria-hidden="true"
            />
        </template>
    </KFooter>
</template>

<script setup>
import { KButton, KFooter, KNavigation } from '@vemedia/karibu'
import { resolveComponent } from 'vue'
import { isExternalStoryblokLink, storyblokLinkHref } from '~~/shared/storyblok-link'
import SiteSearch from '~/components/site-search/site-search.vue'
import { socialIcon, toSocialLink } from '~/utils/social-links'
// Emerald (emrld.ltd) is switched off: the layout no longer loads it at all. The
// scheduling helpers it used stay in `third-party-scripts` so turning it back on is a
// revert rather than a rewrite.
import { ensureOpenWidgetApi, loadExternalScript, waitForOpenWidgetReady } from '~/utils/third-party-scripts'

const { capture } = useTracking()

const route = useRoute()
const config = useState('config')

const currentPageScroll = ref(0)
const scrollMilestones = [10, 25, 50, 75, 90, 100]
const triggeredMilestones = ref([])
const chatLoading = ref(false)
const chatReady = ref(false)
const chatFailed = ref(false)
// The launcher is icon-only, so the label is its accessible name and its tooltip rather
// than visible text. The icon carries the failed state for sighted visitors, who would
// otherwise get no signal that the retry is a retry.
const chatLabel = computed(() => chatLoading.value
    ? 'Opening chat...'
    : chatFailed.value ? 'Try chat again' : 'Chat with us')
const chatIcon = computed(() => (chatFailed.value ? 'tabler:refresh' : 'tabler:message-circle'))

await callOnce(async () => {
    const data = await $fetch('/api/config')
    config.value = data
})

async function openChat() {
    if (chatLoading.value) {
        return
    }

    chatLoading.value = true
    chatFailed.value = false
    const startedAt = performance.now()
    capture('chat_widget_requested')

    window.__ow = window.__ow || {}
    window.__ow.organizationId = 'ec1bec0b-8cf0-47f6-80a2-a440791d65c2'
    window.__ow.integration_name = 'manual_settings'
    window.__ow.product_name = 'openwidget'
    ensureOpenWidgetApi()

    try {
        await loadExternalScript({
            id: 'openwidget-script',
            src: 'https://cdn.openwidget.com/openwidget.js',
        })

        await waitForOpenWidgetReady()
        window.OpenWidget.call('maximize')
        chatReady.value = true
        capture('chat_widget_ready', { duration_ms: Math.round(performance.now() - startedAt) })
    } catch (error) {
        chatFailed.value = true
        capture('chat_widget_load_failed', { message: error instanceof Error ? error.message : String(error) })
    } finally {
        chatLoading.value = false
    }
}

const logoSrc = computed(() => (config.value.media_logo?.filename
    ? `${config.value.media_logo.filename}/m/0x64/filters:format(webp)`
    : '/images/logo.svg'))

function toNavigationItem(item) {
    const href = storyblokLinkHref(item.link)

    return {
        label: item.text?.trim() || '',
        href,
        external: isExternalStoryblokLink(item.link),
        current: href !== '/' ? route.path === href : route.path === '/',
    }
}

// Sub-items were previously dropped entirely and the parent rendered as an
// anchor with no href, so any submenu added in Storyblok became a dead link.
const navigation = computed(() => config.value.top_navigation.map(item => ({
    ...toNavigationItem(item),
    items: (item.sub_menu_items ?? []).map(toNavigationItem),
})))

const nuxtLink = resolveComponent('NuxtLink')

// A childless entry keeps its href: KFooter renders its heading as the link,
// where the old footer dropped the destination and left a dead label. The
// current space holds a single blank menu_item, so unlabelled rows are skipped.
const footerColumns = computed(() => config.value.footer_navigation
    .filter(item => item.text?.trim())
    .map(item => ({
        ...toNavigationItem(item),
        items: (item.sub_menu_items ?? []).map(toNavigationItem),
    })))

const socialLinks = computed(() => (config.value.social_links ?? [])
    .map(item => toSocialLink(item, config.value.brand_name || 'We Are Tanzania'))
    .filter(Boolean))

function onSearch(query) {
    capture('search_submitted', { query, source: 'navigation' })
}

onMounted(() => {
    window.addEventListener('scroll', () => {
        const scrollTop = window.scrollY
        const scrollHeight = document.documentElement.scrollHeight - window.innerHeight
        const scrollPercent = (scrollTop / scrollHeight) * 100

        if (scrollPercent > currentPageScroll.value) {
            currentPageScroll.value = scrollPercent

            scrollMilestones.forEach((milestone) => {
                if (scrollPercent >= milestone && !triggeredMilestones.value.includes(milestone)) {
                    triggeredMilestones.value.push(milestone)

                    capture('page_scroll', { percentage: milestone })

                    if (milestone === 100) {
                        capture('read_complete')
                    }
                }
            })
        }
    })
})

const faviconLinks = computed(() => {
    const asset = config.value.favicon?.filename

    if (!asset) {
        return [{ rel: 'icon', type: 'image/x-icon', href: '/favicon.ico' }]
    }

    // SVG source: one scalable icon covers every modern browser. Storyblok's
    // image service doesn't rasterise SVGs, so keep the static .ico for legacy.
    if (asset.toLowerCase().endsWith('.svg')) {
        return [
            { rel: 'icon', type: 'image/svg+xml', href: asset },
            { rel: 'icon', type: 'image/x-icon', href: '/favicon.ico' },
        ]
    }

    // Raster source: generate every size on the fly via the image service.
    const png = size => `${asset}/m/${size}x${size}/filters:format(png)`

    return [
        { rel: 'icon', type: 'image/png', sizes: '16x16', href: png(16) },
        { rel: 'icon', type: 'image/png', sizes: '32x32', href: png(32) },
        { rel: 'apple-touch-icon', sizes: '180x180', href: png(180) },
        { rel: 'icon', type: 'image/png', sizes: '192x192', href: png(192) },
        { rel: 'icon', type: 'image/png', sizes: '512x512', href: png(512) },
    ]
})

useHead({
    titleTemplate: (title) => {
        if (!title)
            return config.value.brand_name

        return `${title} - ${config.value.brand_name}`
    },
    meta: [
        {
            property: 'og:site_name',
            content: config.value.brand_name,
        },
        {
            property: 'og:locale',
            content: 'en_US',
        },
    ],
    link: faviconLinks,
    style: [
        {
            textContent: `
                :root {
                    --primary: ${config.value.primary_color.color};
                    --secondary: ${config.value.secondary_color.color};
                    --primary-btn: ${config.value.primary_button_color.color};
                    --primary-button-background: ${config.value.primary_button_color.color};
                    --primary-button-color: ${config.value.primary_button_text_color.color};
                }
            `,
            type: 'text/css',
        },
    ],
})
</script>

<style lang="scss">
@use '../scss/main';
@use '../scss/article';

// Karibu owns no page width, so the header's container is set here. It is wider
// than `.page` (1024px) on purpose: the brand and navigation span the page
// maximum while body copy keeps its narrower measure.
.site-header {
    --navigation-max-width: var(--container-4);

    // Height comes from the app token so `html`'s scroll-padding is derived from the same
    // number the bar is drawn at. Unlayered, so it beats the `.k-navigation` default in
    // `@layer karibu.components`.
    --navigation-height: var(--site-header-height);
}

// The footer follows the header's container so the page opens and closes at
// the same width.
.site-footer {
    --footer-max-width: var(--container-4);
}

.chat-trigger {
    position: fixed;
    right: var(--space-4);
    bottom: max(var(--space-4), env(safe-area-inset-bottom));
    z-index: var(--z-sticky);

    // Round, to read as a launcher rather than as a stray square button — and it sits where
    // OpenWidget's own round launcher will appear once the widget takes over.
    .k-button {
        --button-radius: var(--radius-full);
    }
}
</style>
